
import Editor from '@tinymce/tinymce-vue';
import { mapGetters, mapState } from 'vuex';
import gql from 'graphql-tag';
import axios from 'axios';
import { Attachment, S3_Attachment } from '@/schema/attachment';
import { attachmentDownload } from '@/api/attachment';
import { Base64 } from 'js-base64';
import { Risolto_Contact, Risolto_Channel_Endpoint } from '@/generated/graphql';
import MainContacts from '@/components/_MainContacts/index.vue';
import ActionsNotes from '@/components/_ActionsNotes/index.vue';
import Contacts from '@/components/_Contacts/index.vue';
import JsonEditor from '@/components/JsonEditor/index.vue';
import Logging from '@/logging';
import { renderTemplate } from '@/api/templating';
import AttachmentUploader from '@/components/_Attachments/AttachmentUploader.vue';
import EmailForm from '@/components/_Messaging/EmailForm.vue';

interface Message {
    reference: string;
    from_: string;
    subject: string;
    metadata: any;
    archived_at: string | null;
    received_at: string;
    message_recipients: Array<{
        reference: string;
        recipient_by_reference: {
            reference: string;
            legal_entity: string;
            account_id: string;
        };
    }>;
    message_assignees: Array<{
        person_by_reference: {
            reference: string;
            username: string;
        };
    }>;
    message_attachments: Array<{
        reference: string;
        attachment_by_reference: {
            reference: string;
            metadata: {
                key: string;
                name: string;
                bucket: string;
                scheme: string;
                content_type: string;
            };
        };
    }>;
}

const GET_ACTIVE_RECIPIENT_DATA = gql`
    query GetActiveRecipientData($participant: uuid!, $legal_entity: uuid!, $recipient: uuid!) {
        risolto_recipient(
            where: {
                participant: { _eq: $participant },
                legal_entity: { _eq: $legal_entity },
                reference: { _eq: $recipient }
            }
        ) {
            legal_id_type
            type
            legal_id
            reference
            custom_fields
			language_code
			main_email_endpoints: channel_endpoints(
				where: {
					contact: { _is_null: true }
					channel: { _eq: "EMAIL" }
					deactivated_at: { _is_null: true }
				}
				order_by: [{ endpoint: asc }]
			) {
				reference
				channel
				endpoint
			}
			contact_email_endpoints: contacts(
				where: {
					deactivated_at: { _is_null: true }
				}
			) {
				reference
				channel_endpoints(
					where: {
						channel: { _eq: "EMAIL" }
						deactivated_at: { _is_null: true }
					}
				) {
					reference
					channel
					endpoint
				}
			}
        }
    }
`;

const GET_EMAIL_ADDRESS_FROM = gql`
query ViewsComponentsSendNewEmail_emailAddressFrom($participant: uuid!, $legal_entity: uuid!) {
	risolto_legal_entity(
		where: {
			participant: { _eq: $participant }
			reference: { _eq: $legal_entity }
		}
	) {
		email_addresses1: processing_details(path: "outbound_channel.email_addresses")
		email_addresses2: processing_details(path: "outbound_channel.communication.email_addresses")
	}
}
`;

const extractEmails = (input: string | string[] | undefined): string => {
	Logging.log('Extracting emails from:', input);
	if (!input) {
		return '';
	}

	// Convert array input to a single string
	if (Array.isArray(input)) {
		input = input.join(', ');
	}

	// Ensure input is a string
	if (typeof input !== 'string') {
		return '';
	}

	// Updated regex pattern for emails
	const regex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
	const matches: string[] = [];
	let match: RegExpExecArray | null;

	while ((match = regex.exec(input)) !== null) {
		matches.push(match[0].toLowerCase()); // match[0] contains the full email
	}

	Logging.log('Extracted emails:', matches);

	return matches.join(', ');
};

export default {
	components: {
		Editor,
		MainContacts,
		Contacts,
		ActionsNotes,
		JsonEditor,
		AttachmentUploader,
		EmailForm
	},
	props: {
		messageForReplying: {
			type: Object as () => Message,
			default: null
		}
	},
	data() {
		const emailForm = this.messageForReplying
			? {
				from: '',
				to: this.messageForReplying.from_,
				cc: extractEmails(this.messageForReplying.metadata.email.cc) || '',
				bcc: extractEmails(this.messageForReplying.metadata.email.bcc) || '',
				subject: this.messageForReplying.subject
			}
			: {
				from: '',
				to: '',
				cc: '',
				bcc: '',
				subject: ''
			};

		return {
			// Dialog visibility
			fieldDialogFormVisible: false,

			// Recipient and contact information
			currentRecipientResult: undefined,
			inProgressContact: undefined,
			inProgressChannelEndpoint: undefined,

			// Loading state
			loading: false,
			templateLoaded: false,

			// Custom fields
			isShowRecipientCustomFields: false,
			customFieldsJsonEditorData: [{}],

			// Attachments
			currentAttachments: this.messageForReplying
				? this.messageForReplying.message_attachments.map(
					(att) => att.attachment_by_reference.metadata
				)
				: [],
			selectedFile: undefined,

			// Form data
			emailForm: emailForm,
			errorMessage: '',

			// TinyMCE editor
			tinymcecontent: '',
			tinymceScriptSrc: 'https://assets.prod.rslt.be/tinymce/tinymce.min.js',
			tinymceInitOptions: {
				height: 500,
				menubar: false,
				plugins: [
					'advlist',
					'autolink',
					'lists',
					'link',
					'image',
					'charmap',
					'preview',
					'anchor',
					'searchreplace',
					'visualblocks',
					'code',
					'fullscreen',
					'insertdatetime',
					'media',
					'table',
					'code',
					'help',
					'wordcount'
				],
				toolbar:
        'undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media pageembed template link anchor codesample | a11ycheck ltr rtl | showcomments addcomment | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol'
			}
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore', 'selectedLegalEntityStore', 'selectedRecipient', 'loggedInUser']),
		...mapState({
			actionDetailRightPanelCollapsed: (state: any) => state.actions.actionDetailRightPanelCollapsed
		}),
		recipientReference() {
			return this.messageForReplying?.message_recipients[0]?.recipient_by_reference.reference || this.selectedRecipient?.reference;
		},
		hasRecipient() {
			return Boolean(this.recipientReference);
		},
		mainPanelWidth() {
			return this.currentRecipient && !this.actionDetailRightPanelCollapsed ? 17 : 24;
		},
		rightPanelWidth() {
			return this.currentRecipient && !this.actionDetailRightPanelCollapsed ? 7 : 0;
		}
	},
	mounted() {
		Logging.log('Message', this.messageForReplying);
		this.setLoading(true);
	},
	beforeDestroy() {
		const editor = this.$refs.tinymce?.editor;
		if (editor) {
			editor.destroy();
		}
	},
	methods: {
		async loadEmailTemplate() {
			// Only load template once
			if (this.templateLoaded) {
				return;
			}

			this.setLoading(true);

			try {
				const response = await renderTemplate({
					channel: 'EMAIL',
					legal_entity: this.selectedLegalEntityStore,
					tone_of_voice: 'FRIENDLY',
					locale: this.currentRecipientResult?.language_code || 'nl',
					template_id: 'message_email_generic',
					participant: this.selectedParticipantStore,
					template_query_variables: {
						legal_entity: this.selectedLegalEntityStore
					}
				});

				this.tinymcecontent = Base64.decode(response.data.base64);
				this.templateLoaded = true;

				if (!this.messageForReplying && !this.emailForm.to) {
					this.prefillToFieldWithFirstEmail();
				}
			} catch (error) {
				Logging.error('Failed to load email template:', error);
				this.errorMessage = this.$t('messages.failedTemplateFetch').toString();
				this.tinymcecontent = '';
			} finally {
				this.setLoading(false);
			}
		},

		handleClose() {
			const editor = this.$refs.tinymce?.editor;
			if (editor) {
				editor.destroy();
			}
			this.$emit('close');
		},

		toggleRightPanel(value) {
			this.$store.commit('actions/SET_ACTION_DETAIL_RIGHT_PANEL_COLLAPSED', value);
		},

		// TinyMCE editor events
		handleOnDirty() {},
		handleOnChange() {},
		handleOnFocusIn() {},
		handleOnKeyDown() {},
		handleAttachmentAdded(newAttachment: S3_Attachment) {
			this.currentAttachments.push(newAttachment);
		},
		handleRemoveAttachment(attachment: Attachment) {
			const _this = this;
			const index = _this.currentAttachments.indexOf(attachment);

			_this.currentAttachments.splice(index, 1);
			_this.$notify({
				title: _this.$t('success'),
				message: _this.$t('attachmentRemoved'),
				type: 'success'
			});
		},
		async downloadAttachment(attachment: Attachment) {
			attachmentDownload(this.selectedParticipantStore, attachment);
		},

		async submitEmail() {
			const payload = {
				participant: this.selectedParticipantStore,
				legal_entity: this.selectedLegalEntityStore,
				from_address: this.emailForm.from,
				from_name: this.emailAddressFrom.find(addr => addr.email_from === this.emailForm.from)?.email_from_name,
				to: this.emailForm.to,
				subject: this.emailForm.subject,
				body: this.tinymcecontent,
				person: this.loggedInUser.reference,
				recipient: this.currentRecipient?.reference,
				cc: this.emailForm.cc.split(',').map((email) => email.trim()),
				bcc: this.emailForm.bcc.split(',').map((email) => email.trim()),
				attachments: this.currentAttachments,
				message: this.messageForReplying?.reference
			};

			this.errorMessage = '';

			this.$refs.emailForm.validate((valid, errors) => {
				if (!valid) {
					this.errorMessage = this.$t('fillInFields').toString();
					Logging.error(errors);
					return;
				}

				this.setLoading(true);

				let endpoint = this.messageForReplying ? 'plain-email-reply' : 'plain-email';

				axios
					.post(`https://${process.env.VUE_APP_API_ENDPOINT}/mailbox/${endpoint}`, payload, {
						headers: {
							Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}`
						}
					})
					.then((data) => {
						this.$notify({
							title: this.$t('success'),
							message: this.$t('messages.messageSent'),
							type: 'success'
						});
						this.handleClose();
					})
					.catch((error) => {
						this.errorMessage = this.$t('messages.messageNotSent').toString();
						Logging.error(error);
						this.$notify({
							title: this.$t('failed'),
							message: this.$t('messages.messageNotSent'),
							type: 'error'
						});

						this.setLoading(false);
					});
			});
		},

		setLoading(newState: boolean) {
			this.loading = newState;

			const editor = this.$refs.tinymce?.editor;
			if (editor?.mode) {
				editor.mode.set(newState ? 'readonly' : 'design');
			}
		},

		preSelectedContacts(contact) {
			return contact.active_endpoints.some(endpoint => endpoint.reference === 'EMAIL');
		},
		disabledEndpoint(endpoint) {
			return endpoint?.deactivated_at != null;
		},
		disabledContacts(contact) {
			if (!contact) {
				return false;
			}
			return !contact.active_endpoints.some(endpoint => endpoint.channel === 'EMAIL');
		},
		async handleSelectEndpoint(contact: Risolto_Contact, channelEndpoint: Risolto_Channel_Endpoint) {
			if (this.disabledContacts(contact) || this.disabledEndpoint(channelEndpoint) || channelEndpoint.channel !== 'EMAIL') {
				this.$notify({
					title: this.$t('warning'),
					message: this.$t('contact_no_valid_channel_endpoint'),
					type: 'warning'
				});
				return;
			}

			try {
				await this.$confirm(this.$t('Contacts.confirm_select_contact'), {
					confirmButtonText: this.$t('Ok'),
					cancelButtonText: this.$t('cancel'),
					type: 'warning'
				});

				this.fieldDialogFormVisible = true;
				this.inProgressContact = contact;
				this.inProgressChannelEndpoint = channelEndpoint;
			} catch {
				// User cancelled
			}
		},
		continueHandleSelectEndpointForEmail(field, contact: Risolto_Contact, channelEndpoint: Risolto_Channel_Endpoint) {
			this.fieldDialogFormVisible = false;

			const endpoint = channelEndpoint.endpoint;
			if (field === 'to') {
				this.emailForm.to = endpoint;
				this.$store.commit('risolto/SET_SELECTED_CHANNEL_ENDPOINT', channelEndpoint);
				this.$store.commit('risolto/SET_SELECTED_CONTACT', contact);
			} else if (field === 'cc') {
				this.emailForm.cc = this.emailForm.cc ? `${this.emailForm.cc},${endpoint}` : endpoint;
			} else if (field === 'bcc') {
				this.emailForm.bcc = this.emailForm.bcc ? `${this.emailForm.bcc},${endpoint}` : endpoint;
			}
		},
		prefillToFieldWithFirstEmail() {
			if (!this.currentRecipientResult) {
				return;
			}

			const mainEmail = this.currentRecipientResult.main_email_endpoints?.[0]?.endpoint;
			if (mainEmail) {
				this.emailForm.to = mainEmail;
				return;
			}

			const contactEmail = this.currentRecipientResult.contact_email_endpoints
				?.find(contact => contact.channel_endpoints?.length > 0)
				?.channel_endpoints[0]?.endpoint;

			if (contactEmail) {
				this.emailForm.to = contactEmail;
			}
		}
	},
	apollo: {
		emailAddressFrom: {
			query: GET_EMAIL_ADDRESS_FROM,
			variables() {
				return {
					participant: this.selectedParticipantStore,
					legal_entity: this.selectedLegalEntityStore
				};
			},
			update(data) {
				const legalEntity = data.risolto_legal_entity[0];
				const emailAddresses1 = legalEntity.email_addresses1 || [];
				const emailAddresses2 = legalEntity.email_addresses2 || [];

				const allAddresses = [...emailAddresses1, ...emailAddresses2].map(addr => ({
					email_from: addr.email_from,
					email_from_name: addr.email_from_name
				}));

				const uniqueAddressesMap = new Map();
				allAddresses.forEach(addr => {
					if (addr.email_from && !uniqueAddressesMap.has(addr.email_from)) {
						uniqueAddressesMap.set(addr.email_from, addr);
					}
				});

				const uniqueAddresses = Array.from(uniqueAddressesMap.values());

				if (uniqueAddresses.length > 0 && !this.emailForm.from) {
					this.emailForm.from = uniqueAddresses[0].email_from;
				}

				return uniqueAddresses;
			},
			error(error) {
				Logging.error('Error fetching email address:', error);
				this.errorMessage = 'Failed to load email configuration.';
				this.setLoading(false);
			}
		},
		currentRecipient: {
			query: GET_ACTIVE_RECIPIENT_DATA,
			variables() {
				return {
					legal_entity: this.selectedLegalEntityStore,
					participant: this.selectedParticipantStore,
					recipient: this.recipientReference
				};
			},
			update(data) {
				const recipient = data.risolto_recipient?.[0];

				if (recipient) {
					this.customFieldsJsonEditorData = recipient.custom_fields || [{}];
					this.currentRecipientResult = recipient;
				}

				return recipient || null;
			},
			result() {
				this.loadEmailTemplate();
			},
			error(error) {
				Logging.error('Error fetching recipient:', error);
				this.errorMessage = 'Failed to load recipient data.';
				this.setLoading(false);
			},
			skip() {
				const shouldSkip = !this.hasRecipient;
				// Load template if we're skipping the recipient query
				if (shouldSkip) {
					this.loadEmailTemplate();
				}
				return shouldSkip;
			}
		}
	}
};
